import ApplicationController from '../application_controller.js';
import StimulusReflex from 'stimulus_reflex'
import { Controller } from "stimulus";
import AudioConnectionController from './webrtc/audio_connection.js';
import DeviceManagementController from './webrtc/device_management.js';
import VideoConnectionController from './webrtc/video_connection.js';
import QualityController from './webrtc/quality.js';
import ScreenshareConnection from './webrtc/screenshare_connection.js';
import BackgroundBlur from './webrtc/background_blur.js';
import { Janus } from 'janus-gateway';
import { Socket } from "phoenix";
import throttle from 'lodash/throttle';
import rollbar from '../../js/rollbar';

// Remembers the background choice on this device between calls
const BACKGROUND_STORAGE_KEY = 'togetherly.videoBackground';





export default class extends ApplicationController {

    // Stimulus values - ICE servers passed from server
    static values = { iceServers: Array }

    //Initialize variables

    video = false;
    callObject = {};

    initialize() {
        StimulusReflex.register(this);

        // Skip WebRTC initialization in simulation mode
        this.simulationMode = this.element.getAttribute('data-simulation_mode') === 'true';
        if (this.simulationMode) {
            console.debug("Simulation mode: skipping WebRTC initialization");
            return;
        }

        // Skip WebRTC initialization in test mode
        this.testMode = this.element.getAttribute('data-test_mode') === 'true';
        if (this.testMode) {
            console.debug("Test mode: skipping WebRTC initialization");
            return;
        }

        this.audioConnectionController = new AudioConnectionController();
        this.deviceManagementController = new DeviceManagementController();
        this.videoConnectionController = new VideoConnectionController();
        this.qualityController = new QualityController();
        // Facilitator screen sharing: a second Janus publisher with its own handle.
        // onStopped fires however a share ends (button, browser "Stop sharing", PC drop).
        this.screenshareConnection = new ScreenshareConnection({
            onStopped: () => {
                this.stimulate("LiveCall#screenshare_stopped");
                document.dispatchEvent(new Event('screenshareIdle'));
            }
        });
        window.addEventListener('beforeunload', () => this.screenshareConnection.stop());

        this.callObject = {
            audioDeviceId: null,
            audioDeviceLabel: null,
            videoDeviceId: null,
            videoDeviceLabel: null,
            conferencePin: this.element.getAttribute('data-conference_pin'),
            stasisApp: this.element.getAttribute('data-stasis_app'),
            scheduled_session_id: this.element.getAttribute('data-scheduled_session_id'),
            contactId: this.element.getAttribute('data-contact_id'),
            joinBy: this.element.getAttribute('data-join_by'),
            dspEnabled: this.element.getAttribute('data-dsp_enabled'),
            server: "wss://" + this.element.getAttribute('data-wss') + "?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6InNpbW9uQG1lZXR1cGNhbGwuY29tIiwiZXhwIjoxNjc1MTkwNjY5fQ.-LOKIBPKMY8hchH24Fkbm5LD9WYn4AzEuftiRkGLr7A",
            localVideoStream: null,
            videoBackground: "none" // "none" or "blur" (AccountRole :background_blur)
        }
        this.setUpBackgroundBlur();

        if (this.element.getAttribute('data-join_by') == 'video') {
            this.video = true;
        }

        // Configure Rollbar with contact data
        rollbar.configure({
            payload: {
                person: {
                    id: this.element.getAttribute('data-contact_id')
                },
                request: {
                    scheduled_session_id: this.callObject.scheduled_session_id
                }
            }
            });


        this.waitForActionCableConnection()
            .then(() => {
                this.syncBackgroundChoice();
                console.debug("ActionCable connection is open, checking device permissions...");
                this.deviceManagementController.checkDevicePermission({ audio: true, video: false })
                .then(() => {
                    console.debug("Device access granted, getting devices...");
                    this.populateDevices();
                })
                .catch((error) => {
                    console.error("Device permission denied", error);
                    this.stimulate("Webrtc#state",error);
                });
            })

            .catch((error) => {
                console.debug("ActionCable connection failed", error);
                this.refreshPage();
            });
    }
    
    // Wait for the ActionCable connection to be open so stimulate etc work properly
    // Note: In StimulusReflex 3.5.x, this.StimulusReflex no longer has a .subscription property
    waitForActionCableConnection() {
        return new Promise((resolve) => {
            const checkConnection = () => {
                console.debug("Checking ActionCable connection...", this.isActionCableConnectionOpen());
                if (this.isActionCableConnectionOpen() && this.StimulusReflex) {
                    resolve();
                } else {
                    setTimeout(checkConnection, 1000);
                }
            };
            checkConnection();
        });
    }


    requestDevicePermission() {
        let self = this;
        this.stimulate("Webrtc#state","populatingDevices");
        this.deviceManagementController.checkDevicePermission({ audio: true, video: self.video })
            .catch((error) => {
                rollbar.debug("Device access denied, requesting...", error);
                return this.deviceManagementController.requestDevicePermission({ audio: true, video: self.video });
            })
            .then(() => {
                rollbar.debug("Device access granted, getting devices...");
                this.populateDevices();
            })
            .catch((error) => {
                console.error("Device permission denied", error);
                this.stimulate("Webrtc#state","noDevices");
            });
    }

    populateDevices() {
        let self = this;
        this.deviceManagementController.getDevices()
            .then((devices) => {
                rollbar.debug("Available devices:", devices);
                self.stimulate("Webrtc#devices",devices);
                
            })
            .then(() => {
                if (self.video) {
                    self.stimulate("Webrtc#state","activating");
                    this.setLocalVideoStream()
                        .then(stream => {
                            rollbar.debug("Stream set successfully", stream);
                            self.stimulate("Webrtc#state","ready");
                        })
                        .catch(error => {
                            rollbar.error("Failed to set local video stream", error);
                           
                        });
                }else{
                    self.stimulate("Webrtc#state","ready");
                }
            })
            .catch((error) => {
                console.error("Error populating devices", error);
                self.stimulate("Webrtc#state","noDevices");
            });
    }

    refreshPage() {
        window.location.reload();
    }

    // Called when microphone is changed
    selectMic() {     
        this.callObject.audioDeviceId = this.element.querySelector('#mic_select').value;
        this.callObject.audioDeviceLabel = this.element.querySelector('#mic_select').options[this.element.querySelector('#mic_select').selectedIndex].text;
        rollbar.debug('Mic changed',this.callObject.audioDeviceId, this.callObject.audioDeviceLabel)
        this.stimulate("Webrtc#set_last_used_mic", this.callObject.audioDeviceId);
    }
    
    // Called when camera is changed
    selectCam() {     
        this.callObject.videoDeviceId = this.element.querySelector('#cam_select').value;
        this.callObject.videoDeviceLabel = this.element.querySelector('#cam_select').options[this.element.querySelector('#cam_select').selectedIndex].text;
        rollbar.debug('Cam changed',this.callObject.videoDeviceId, this.callObject.videoDeviceLabel);
        this.stimulate("Webrtc#set_last_used_cam", this.callObject.videoDeviceId);
        this.setLocalVideoStream();
    }

    // --- Background blur ----------------------------------------------------

    setUpBackgroundBlur() {
        const available = this.element.getAttribute('data-background_blur_available') === 'true';
        this.backgroundBlurSupported = available && BackgroundBlur.isSupported();
        if (available && !this.backgroundBlurSupported) {
            // A class on <html> survives page morphs; CSS hides the select
            document.documentElement.classList.add('background-blur-unsupported');
        }
        this.serverVideoBackground = this.element.getAttribute('data-video_background') || null;
        let saved = this.serverVideoBackground;
        if (!saved) {
            try { saved = window.localStorage.getItem(BACKGROUND_STORAGE_KEY); } catch (e) { saved = null; }
        }
        this.callObject.videoBackground = this.backgroundBlurSupported && saved === 'blur' ? 'blur' : 'none';
    }

    // Tell the server about a choice remembered in localStorage, so page morphs
    // render the select with the right value
    syncBackgroundChoice() {
        if (this.backgroundBlurSupported && this.callObject.videoBackground !== (this.serverVideoBackground || 'none')) {
            this.stimulate("Webrtc#set_background", this.callObject.videoBackground);
        }
    }

    saveBackgroundChoice(value) {
        this.callObject.videoBackground = value;
        this.serverVideoBackground = value;
        try { window.localStorage.setItem(BACKGROUND_STORAGE_KEY, value); } catch (e) { /* private mode */ }
        const backgroundSelect = this.element.querySelector('#background_select');
        if (backgroundSelect) backgroundSelect.value = value;
        this.stimulate("Webrtc#set_background", value);
    }

    // Called when the background select is changed on the join screen
    selectBackground() {
        const backgroundSelect = this.element.querySelector('#background_select');
        if (!backgroundSelect) return;
        const value = backgroundSelect.value === 'blur' && this.backgroundBlurSupported ? 'blur' : 'none';
        rollbar.debug('Background changed', value);
        this.saveBackgroundChoice(value);
        this.setLocalVideoStream();
    }

    // The camera stream to preview and publish: blurred if that's the choice,
    // otherwise (or if blur can't start) the plain camera
    async getCameraStream(videoDeviceId) {
        const cameraStream = await this.videoConnectionController.getLocalVideoStream(videoDeviceId);
        if (this.callObject.videoBackground !== 'blur') {
            return cameraStream;
        }
        const blur = new BackgroundBlur({
            onFallback: (reason) => this.handleBackgroundBlurFallback(blur, reason)
        });
        try {
            return await blur.start(cameraStream);
        } catch (error) {
            rollbar.warning("Background blur failed to start", error);
            this.saveBackgroundChoice('none');
            window.notyf.open({message: "Background blur isn't available on this device.", type: 'alert', duration: 5000, dismissible: true});
            return cameraStream;
        }
    }

    // Blur got too slow or kept failing: switch to the plain camera, in the
    // preview and, if published, in the call itself (without renegotiating)
    handleBackgroundBlurFallback(blur, reason) {
        const blurredStream = blur.outputStream;
        if (!blurredStream || this.callObject.localVideoStream !== blurredStream) {
            return; // An old instance; the current stream has already moved on
        }
        const cameraStream = blur.cameraStream;
        rollbar.warning("Background blur fell back to the plain camera", { reason });
        this.saveBackgroundChoice('none');
        window.notyf.open({
            message: reason === 'overload'
                ? "Background blur was turned off because it was slowing your device down."
                : "Background blur stopped working, so it has been turned off.",
            type: 'alert', duration: 6000, dismissible: true
        });

        const switchToCamera = () => {
            blur.stop({ keepCamera: true });
            this.callObject.localVideoStream = cameraStream;
            const localPlayer = this.element.querySelector('#localvideo');
            if (localPlayer && localPlayer.srcObject === blurredStream) {
                localPlayer.srcObject = cameraStream;
            }
            if (window.localVideoStream === blurredStream) {
                window.localVideoStream = cameraStream;
                document.dispatchEvent(new Event('localVideoStreamChanged'));
            }
        };

        const published = this.pluginHandle && window.localVideoStream === blurredStream;
        if (!published) {
            switchToCamera();
            return;
        }
        // Janus stops the track it replaces, and stopping the blurred track
        // stops the camera too; dontStop leaves that to switchToCamera
        const blurredTrack = blurredStream.getVideoTracks()[0];
        blurredTrack.dontStop = true;
        this.pluginHandle.replaceTracks({
            tracks: [{ type: 'video', capture: cameraStream.getVideoTracks()[0], replace: true }],
            success: switchToCamera,
            error: (error) => {
                // Leave blur running rather than drop the video
                blurredTrack.dontStop = false;
                rollbar.error("Couldn't swap the plain camera back in after background blur", error);
            }
        });
    }

    stopLocalVideoStream() {
        if (this.callObject.localVideoStream) {
            // For a blurred stream this also stops the camera (see BackgroundBlur)
            this.callObject.localVideoStream.getTracks().forEach(track => track.stop());
            this.callObject.localVideoStream = null;
        }
    }

    setLocalVideoStream() {
        return new Promise((resolve, reject) => {
            const waitForElements = () => {
                const camSelectElement = this.element.querySelector('#cam_select');
                const localPlayer = this.element.querySelector('#localvideo');
    
                if (camSelectElement && localPlayer) {
                    let videoDeviceId = camSelectElement.value;
                    // Release the previous preview first (camera switch, background
                    // change, after hangup), or the old camera stays on
                    this.stopLocalVideoStream();
                    const backgroundSelect = this.element.querySelector('#background_select');
                    if (backgroundSelect) backgroundSelect.value = this.callObject.videoBackground;
                    // A newer preview request (quick camera/background changes)
                    // makes this one stale: stop its stream instead of using it
                    const generation = this.previewGeneration = (this.previewGeneration || 0) + 1;
                    this.getCameraStream(videoDeviceId)
                        .then((stream) => {
                            if (generation !== this.previewGeneration) {
                                stream.getTracks().forEach(track => track.stop());
                                resolve(this.callObject.localVideoStream);
                                return;
                            }
                            console.debug("Local video stream ready", stream);
                            localPlayer.srcObject = stream;
                            this.callObject.localVideoStream = stream;
                            resolve(stream);
                        })
                        .catch((error) => {
                            this.stimulate("Webrtc#state", "camError");
                            console.error("Error getting local video stream", error);
                            reject(error);
                        });
                } else {
                    setTimeout(waitForElements, 100); // Retry after 100ms
                }
            };
    
            waitForElements();
        });
    }

    // Called when the join button is clicked
    joinOnline() {
        const micSelectElement = this.element.querySelector('#mic_select');
        const camSelectElement = this.element.querySelector('#cam_select');
        
        
        // TODO: Probably should error if these aren't available for some reason
        this.callObject.audioDeviceId = micSelectElement ? micSelectElement.value : null;
        this.callObject.videoDeviceId = camSelectElement ? camSelectElement.value : null;

        this.stimulate("Webrtc#state","connecting");

        rollbar.debug("Joining online", this.callObject);
        Janus.init({
            debug: true,
            callback: () => {
                console.debug("Connecting to Janus server", this.server);
                window.janus = new Janus(
                    {
                        server: this.callObject.server,
                        iceServers: this.iceServersValue,
                        success: () => {
                            //initialise janus
                            Janus.log("Janus initialized")
                            

                            //Attach the Janus video plugin if we are joining a video call
                            if (this.video) {
                                this.stimulate("Webrtc#video_state","publishing");
                                this.videoConnectionController.attachVideoRoom()
                                .then((pluginHandle) => {
                                    rollbar.debug("Video room attached successfully", pluginHandle);
                                    //Join the video room
                                    this.videoConnectionController.joinVideoRoom(pluginHandle, this.callObject);
                                    return pluginHandle;
                                })
                                .then((pluginHandle) => {
                                    rollbar.debug("Video room joined successfully");
                                    //Start streaming my video
                                    return this.videoConnectionController.startMyVideo(pluginHandle, this.callObject)
                                    .then(() => {
                                        rollbar.debug("My video started successfully");
                                        return pluginHandle;
                                    });
                                })
                                .then((pluginHandle) => {
                                    //Connect to the remote incoming video streams
                                    this.videoConnectionController.connectRemoteStreams(pluginHandle, this.callObject);
                                    return pluginHandle;
                                })
                                .then((pluginHandle) => {
                                    rollbar.debug("Remote video streaming connected successfully");
                                    this.stimulate("Webrtc#video_state","published");

                                    //Listen for video events e.g. camera on and off
                                    this.handleVideoEvents(pluginHandle, this.callObject);

                                    //Set up a promise to listen for audio call hangups so we can leave the video room
                                    //and stop broadcasting when the audio call is hung up
                                    this.audioConnectionController.monitorCallState()
                                        .then(() => {
                                            console.debug("Audio call hung up, leaving video room");
                                            this.screenshareConnection.stop();
                                            this.videoConnectionController.leaveVideoRoom(pluginHandle, this.callObject)
                                                .then(() => {
                                                    rollbar.debug("Left video room successfully");
                                                })
                                                .catch((error) => {
                                                    rollbar.error("Error leaving video room", error);
                                                });
                                        })
                                        .catch((error) => {
                                            console.debug("Audio call hung up, leaving video room");
                                            this.screenshareConnection.stop();
                                            this.videoConnectionController.leaveVideoRoom(pluginHandle, this.callObject)
                                                .then(() => {
                                                    rollbar.debug("Left video room successfully");
                                                })
                                                .catch((error) => {
                                                    rollbar.error("Error leaving video room", error);
                                                });
                                        });
                                    })
                                .catch((error) => {
                                    this.recoverFromVideoPublishFailure(error);
                                });
                            }

                            
                            //Attach the SIP plugin for the audio call
                            this.audioConnectionController.attachSIP()
                                .then((pluginHandle) => {
                                    rollbar.debug("SIP plugin attached successfully", pluginHandle);
                                    //Register with the Janus server
                                    this.audioConnectionController.registerSip(pluginHandle);
                                    return pluginHandle;
                                })
                                .then((pluginHandle) => {
                                    rollbar.debug("SIP plugin registered successfully");
                                    //Make the audio call
                                    return this.audioConnectionController.makeAudioCall(pluginHandle, this.callObject)
                                        .then(() => {
                                            rollbar.debug("Audio call made successfully");
                                            return pluginHandle;
                                        });
                                })
                                .then((pluginHandle) => {
                                    console.debug("Audio call answered successfully");
                                    setTimeout(() => {
                                        this.stimulate("Webrtc#state","connected");
                                    }, 1000);

                                    //Monitor the audio quality
                                    this.qualityController.monitorAudioStats(pluginHandle);

                                    //Listen for audio quality events
                                    this.handleAudioEvents(pluginHandle, this.callObject);

                                    //Set up a promise to listen for audio call hangups
                                    this.audioConnectionController.monitorCallState()
                                        .then(() => {
                                            rollbar.debug("Audio call hung up cleanly");
                                            this.handleHangup();
                                        })
                                        .catch((error) => {
                                            rollbar.error("Audio call hung up with error", error);
                                            this.handleHangup();
                                        });
                                })
                                .then(() => {
                                    
                                })
                                .catch((error) => {
                                    this.stimulate("Webrtc#state","rtcUnavailable");
                                    rollbar.error("Error making SIP audio call", error);
                                });
                        },
                        error: (error) => {
                            console.error("rtcUnavailable error", error);
                            this.stimulate("Webrtc#state","rtcUnavailable");
                            rollbar.error("Janus initialization error", error);
                            setTimeout(() => { 
                                //?
                            }, 5000);
                        }
                    }
                );       
            }   
        });
    }

    handleHangup() {
        if (this.screenshareConnection) {
            this.screenshareConnection.stop();
        }
        this.stimulate("Webrtc#state","disconnected");
        this.stimulate("Webrtc#video_state","disconnected");
        this.setLocalVideoStream();
        // Remove listeners using the class field references
        document.removeEventListener('unpublishMyVideo', this.handleUnpublishMyVideo);
        document.removeEventListener('publishMyVideo', this.handlePublishMyVideo);
        document.removeEventListener('startScreenshare', this.handleStartScreenshare);
        document.removeEventListener('stopScreenshare', this.handleStopScreenshare);
    }

    handleStartScreenshare = (event) => {
        this.screenshareConnection.start(window.janus, this.callObject)
            .then(() => {
                if (this.screenshareConnection.isActive) {
                    // Janus has confirmed the publish; tell the server so every
                    // client re-renders into the screenshare layout
                    this.stimulate("LiveCall#screenshare_started");
                } else {
                    document.dispatchEvent(new Event('screenshareIdle'));
                }
            })
            .catch((error) => {
                document.dispatchEvent(new Event('screenshareIdle'));
                // The user closed the browser's picker or the OS denied permission
                if (error && error.name === 'NotAllowedError') {
                    return;
                }
                if (error && error.message === "Someone else is already sharing their screen") {
                    window.notyf.open({message: "Someone else is already sharing their screen.", type: 'alert', duration: 5000, dismissible: true});
                    return;
                }
                window.notyf.open({message: "We couldn't share your screen. Please try again.", type: 'alert', duration: 5000, dismissible: true});
                rollbar.error("Error starting screen share", error);
            });
    }

    handleStopScreenshare = (event) => {
        if (this.screenshareConnection.isActive) {
            // onStopped notifies the server once the feed has left the room
            this.screenshareConnection.stop();
        } else {
            // Stale button (e.g. share already ended): ask the server to re-render
            this.stimulate("LiveCall#screenshare_stopped");
            document.dispatchEvent(new Event('screenshareIdle'));
        }
    }

    handleUnpublishMyVideo = (event) => {
        // Stop the local video stream tracks to release the camera
        if (this.callObject.localVideoStream) {
            this.callObject.localVideoStream.getTracks().forEach(track => track.stop());
            this.callObject.localVideoStream = null;
        }

        this.videoConnectionController.stopMyVideo(this.pluginHandle)
            .then(() => {
                console.debug("My video stopped successfully");
                this.stimulate("Webrtc#video_state","unpublished");
            })
            .catch((error) => {
                console.error("Error stopping my video", error);
            });
    }

    handlePublishMyVideo = (event) => {
        this.stimulate("Webrtc#video_state","publishing");
        let videoDeviceId = this.callObject.videoDeviceId;
        this.getCameraStream(videoDeviceId)
            .then((stream) => {
                console.debug("Local video stream ready", stream);
                this.callObject.localVideoStream = stream;
                // Update the local video preview element
                const localPlayer = this.element.querySelector('#localvideo');
                if (localPlayer) {
                    localPlayer.srcObject = stream;
                }
                return this.callObject;
            })
            .then((callObject) => {
                return this.videoConnectionController.startMyVideo(this.pluginHandle, callObject);
            })
            .then(() => {
                rollbar.debug("Video republished successfully");
                this.stimulate("Webrtc#video_state","published");
            })
            .catch((error) => {
                window.notyf.open({message: "There has been an error publishing your video. Please try again.", type: 'alert', duration: 5000, dismissible: true});
                rollbar.error("Error republishing local video stream", error);
                this.stimulate("Webrtc#video_state", "unpublished");
            });
    }

    handleVideoEvents(pluginHandle, callObject) {        
        let self = this;

        // Store pluginHandle and callObject as class properties
        this.pluginHandle = pluginHandle;
        this.callObject = callObject;

        // Add listeners using the class field references
        document.addEventListener('unpublishMyVideo', this.handleUnpublishMyVideo);
        document.addEventListener('publishMyVideo', this.handlePublishMyVideo);
        document.removeEventListener('startScreenshare', this.handleStartScreenshare);
        document.removeEventListener('stopScreenshare', this.handleStopScreenshare);
        document.addEventListener('startScreenshare', this.handleStartScreenshare);
        document.addEventListener('stopScreenshare', this.handleStopScreenshare);
        

        //Handle  quality related events
        document.removeEventListener('audioEvent', this.handleQualityEvent);
        this.handleQualityEvent = (event) => {
            switch(event.detail.type) {
                case 'terribleUpload':
                    console.warn("Disable simulcast", event.detail);
                    //this.videoConnectionController.disableSimulcast(pluginHandle);
                    break;
                case 'terribleDownload':
                    // fire a custom event that will be picked up by the videoConnectionController remote stream plugin handler
                    console.warn("Use low resolution stream", event.detail);
                    let loResEvent = new CustomEvent('switchToLowRes', { detail: { message: 'Switch to low resolution' } });
                    document.dispatchEvent(loResEvent);
                    break;
            }
        };
        document.addEventListener('audioEvent', this.handleQualityEvent);
    }

    //-------------------------------------------------------------------------
    // Video publish failure recovery.
    // Everything below is reached ONLY from the .catch at the end of the video
    // promise chain. A client whose publish succeeds never runs any of it.
    //-------------------------------------------------------------------------

    // Work out how far the video chain got, using only state the success path
    // already writes. Adds no bookkeeping to the success path.
    videoFailureStage() {
        const videoConnection = this.videoConnectionController;
        if (!videoConnection || !videoConnection.pluginHandle || videoConnection.pluginHandle.detached) {
            return 'attach';
        }
        if (videoConnection.mypvtid === undefined || videoConnection.mypvtid === null) {
            return 'join';
        }
        return 'publish';
    }

    // Fire and forget. Must never throw - it is the last line of defence.
    recoverFromVideoPublishFailure(error) {
        let stage;
        try {
            stage = this.videoFailureStage();
        } catch (e) {
            stage = 'attach';
        }

        const videoConnection = this.videoConnectionController;
        const pluginHandle = videoConnection && videoConnection.pluginHandle;

        // Never attached: no handle to subscribe with, nothing to tear down.
        if (stage === 'attach') {
            rollbar.error("Video room attach failed", error, { stage: stage, ua: navigator.userAgent });
            this.showVideoFailureNotice();
            this.stimulate("Webrtc#video_state", "unpublished");
            return;
        }

        try {
            // Restore what the aborted chain skipped, before any diagnostics.
            // handleVideoEvents assigns this.pluginHandle and wires the camera
            // toggle listeners, so the button works instead of hanging.
            this.handleVideoEvents(pluginHandle, this.callObject);

            // Subscribing to other people is independent of our own publish -
            // it needs the handle and mypvtid, both of which we have by now.
            // Skipped at the 'join' stage because an unset mypvtid would send
            // private_id: NaN into a room created with require_pvtid: true.
            if (stage === 'publish') {
                videoConnection.connectRemoteStreams(pluginHandle, this.callObject);
            }

            // Keep hangup teardown working - monitorCallState is memoised, so
            // this just attaches another handler to the existing promise.
            const leaveRoom = () => {
                videoConnection.leaveVideoRoom(pluginHandle, this.callObject)
                    .catch((leaveError) => {
                        rollbar.warn("Error leaving video room after publish failure", leaveError);
                    });
            };
            this.audioConnectionController.monitorCallState().then(leaveRoom).catch(leaveRoom);
        } catch (recoveryError) {
            rollbar.error("Video publish recovery failed", recoveryError, { stage: stage });
        }

        this.qualityController.captureVideoPublishDiagnostics({
            pluginHandle: pluginHandle,
            localStream: this.callObject.localVideoStream,
            error: error,
            stage: stage
        })
        .then((diagnostics) => this.applyVideoFailureOutcome(stage, error, diagnostics))
        .catch((diagnosticsError) => this.applyVideoFailureOutcome(stage, error, {
            verdict: 'diagnostics_failed',
            diagnosticsError: String(diagnosticsError)
        }));
    }

    applyVideoFailureOutcome(stage, error, diagnostics) {
        rollbar.error("Video publish failed: " + diagnostics.verdict, error, diagnostics);

        // The device was just slow, and video is genuinely flowing. Promote it
        // rather than tearing down a working publish.
        if (diagnostics.verdict === 'late_publish') {
            this.stimulate("Webrtc#video_state", "published");
            return;
        }

        // Release the camera so a retry gets a clean getUserMedia.
        if (this.callObject.localVideoStream) {
            this.callObject.localVideoStream.getTracks().forEach((track) => track.stop());
            this.callObject.localVideoStream = null;
        }

        // Deliberately not awaited: stopMyVideo only settles when Janus sends
        // webrtcState(false), which may never arrive. The UI must not wait on it.
        if (diagnostics.hadPeerConnection) {
            this.videoConnectionController.stopMyVideo(this.pluginHandle).catch(() => {});
        }

        this.showVideoFailureNotice();
        this.stimulate("Webrtc#video_state", "unpublished");
    }

    showVideoFailureNotice() {
        window.notyf.open({message: "There has been an error connecting your video. Please refresh and try again.", type: 'alert'});
    }

    handleAudioEvents(pluginHandle, callObject) {
        // Remove existing listener if any
        document.removeEventListener('audioEvent', this.handleAudioEvent);
    
        // Create a new handler function
        this.handleAudioEvent = (event) => {
            window.notyf.dismissAll();

            switch(event.detail.type) {
                case 'poorUpload':
                    rollbar.warn("Poor upload detected", event.detail);
                    window.notyf.open({message: "Your internet connection quality is poor, people may struggle to hear you.", type: 'alert'});
                    break;
                case 'terribleUpload':
                    rollbar.warn("Terrible upload detected", event.detail);
                    window.notyf.open({message: "Your internet connection quality is VERY poor, people may struggle to hear you.", type: 'alert'});
                    break;
                case 'poorDownload':
                    rollbar.warn("Poor download detected", event.detail);
                    window.notyf.open({message: "Your internet connection quality is poor, you may struggle to hear others.", type: 'alert'});
                    break;
                case 'terribleDownload':
                    rollbar.warn("Terrible download detected", event.detail);
                    window.notyf.open({message: "Your internet connection quality is VERY poor, you may struggle to hear others.", type: 'alert'});
                    break;
                case 'connectionRestored':
                    rollbar.warn("Connection restored", event.detail);
                    window.notyf.open({message: "Your internet connection is now stable.", type: 'info'});
                    break;
            }
        };
    
        // Add the new listener
        document.addEventListener('audioEvent', this.handleAudioEvent);
    }
}
